import { useState } from "react";

import { useTranslation } from "react-i18next";

import type { Item } from "@/api/items";
import { useBulkAssignPlanningPolicy } from "@/api/planningConfig";
import { usePlanningPolicies } from "@/api/planningPolicy";
import { LocationCombobox } from "@/components/entity-select/LocationCombobox";
import { PlanningPolicyCombobox } from "@/components/entity-select/PlanningPolicyCombobox";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  items: Item[];
  onClose: () => void;
}

/** Bulk-assign one policy to the selected items at one location. Mount per open. */
export function AssignPolicyDialog({ items, onClose }: Props) {
  const { t } = useTranslation();
  const [locationId, setLocationId] = useState<number | null>(null);
  const [policyId, setPolicyId] = useState<number | null>(null);
  const [minStock, setMinStock] = useState<number | null>(null);
  const [maxStock, setMaxStock] = useState<number | null>(null);
  const policies = usePlanningPolicies();
  const assign = useBulkAssignPlanningPolicy();

  const isMinMax =
    policies.data?.find((p) => p.id === policyId)?.regime === "min_max";
  const valid =
    locationId !== null &&
    (!isMinMax ||
      (minStock !== null && maxStock !== null && minStock < maxStock));

  async function submit() {
    if (locationId === null) return;
    await assign.mutateAsync({
      itemIds: items.map((i) => i.id),
      location: locationId,
      planningPolicy: policyId,
      ...(isMinMax ? { minStock, maxStock } : {}),
    });
    onClose();
  }

  const numberInput = (
    id: string,
    label: string,
    value: number | null,
    set: (v: number | null) => void,
  ) => (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        value={value ?? ""}
        onChange={(e) =>
          set(e.target.value === "" ? null : Number(e.target.value))
        }
      />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t("items.assignPolicyDialog.title", { count: items.length })}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label>{t("items.assignPolicyDialog.location")}</Label>
            <LocationCombobox value={locationId} onChange={setLocationId} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>{t("items.assignPolicyDialog.policy")}</Label>
            <PlanningPolicyCombobox value={policyId} onChange={setPolicyId} />
          </div>
          {isMinMax && (
            <div className="grid grid-cols-2 gap-3">
              {numberInput(
                "assign-min-stock",
                t("items.assignPolicyDialog.min"),
                minStock,
                setMinStock,
              )}
              {numberInput(
                "assign-max-stock",
                t("items.assignPolicyDialog.max"),
                maxStock,
                setMaxStock,
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t("common.buttons.cancel")}
          </Button>
          <Button onClick={submit} disabled={!valid || assign.isPending}>
            {t("items.assignPolicyDialog.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
